import { useRef } from 'react';

import {
  useIndustrialConnectorSamples,
  useIndustrialMotorSamples,
} from '../useIndustrialPageData';
import * as S from './IndustrialSampleShowcase.styles';
import IndustrialWaveformPlayer from './IndustrialWaveformPlayer';
import useDefectProgress from './useDefectProgress';

type Translate = (key: string) => string;

/**
 * ### IndustrialSampleShowcase
 *
 * Places the short connector-click comparison before the longer motor-stage
 * sequence. The order matches the page's primary
 * connector-inspection message while keeping the existing waveform and
 * defect-progress behavior for the motor samples.
 *
 * @param props Translator for the current page locale
 * @returns The ordered Industrial sample sections
 */
export default function IndustrialSampleShowcase({ t }: { t: Translate }) {
  const motorSectionRef = useRef<HTMLElement | null>(null);
  const motorSamples = useIndustrialMotorSamples(t);
  const connectorSamples = useIndustrialConnectorSamples(t);
  const { currentStageIndex } = useDefectProgress(motorSectionRef);
  const activeMotorSample = motorSamples[currentStageIndex] ?? motorSamples[0];

  return (
    <div className={S.showcaseContainer} data-industrial-sample-showcase>
      <section className={S.sampleSection}>
        <div className={S.sectionHeaderColumn}>
          <div className={S.sectionHeader} data-sample-sticky-header>
            <span className={S.sectionEyebrow}>{t('Click Sound')}</span>
            <h4 className={S.sectionTitle}>{t('커넥터 체결 클릭음 2종')}</h4>
            <p className={S.sectionDescription}>
              {t(
                '공장 배경 소음 속에서 커넥터 체결 클릭 A/B의 차이를 들어보세요',
              )}
            </p>
          </div>
        </div>
        <ul className={S.sampleList}>
          {connectorSamples.map((sample) => (
            <li className={S.sampleRow} key={sample.key}>
              <div className={S.sampleMeta}>
                <span className={S.sampleIndex}>{sample.key}</span>
                <div className={S.sampleText}>
                  <h5 className={S.sampleTitle}>{sample.title}</h5>
                  <p className={S.sampleDescription}>{sample.description}</p>
                </div>
              </div>
              <IndustrialWaveformPlayer
                sampleId={sample.analyticsId}
                src={sample.src}
                transientSrc={sample.transientSrc}
                label={sample.title}
                t={t}
              />
            </li>
          ))}
        </ul>
      </section>

      <section
        className={S.sampleSection}
        ref={motorSectionRef}
        data-motor-sample-section
      >
        <div className={S.sectionHeaderColumn}>
          <div className={S.sectionHeader} data-sample-sticky-header>
            <span className={S.sectionEyebrow}>{t('Motor Sound')}</span>
            <h4 className={S.sectionTitle}>
              {t('정상에서 불량까지 이어지는 모터 5단계')}
            </h4>
            <p className={S.sectionDescription}>
              {t(
                '회전체 작동음이 정상 구간에서 이상 단계로 변하는 흐름을 같은 기준 파형으로 확인할 수 있어요',
              )}
            </p>
            <div
              className={S.verticalDefectScale}
              aria-hidden="true"
              data-defect-scale
            >
              <div className={S.defectScaleSummary}>
                <span className={S.verticalScaleLabel()}>{t('정상')}</span>
                <strong className={S.defectMeterStage}>
                  {activeMotorSample?.stage ?? '01'}
                </strong>
              </div>
              <div className={S.verticalScaleBody}>
                <div className={S.verticalScaleTrack} data-scale-track>
                  <div className={S.verticalScaleFill} />
                </div>
                <ol className={S.verticalScaleMarks}>
                  {motorSamples.map((sample, index) => (
                    <li
                      className={S.verticalScaleMark}
                      key={sample.stage}
                      data-scale-mark={index}
                    >
                      <span>{sample.stage}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className={S.defectScaleFooter}>
                <span className={S.verticalScaleLabel(true)} data-defect-label>
                  {t('불량')}
                </span>
              </div>
            </div>
          </div>
        </div>
        <ul className={S.sampleList}>
          {motorSamples.map((sample) => (
            <li
              className={S.sampleRow}
              key={sample.stage}
              data-motor-sample-row
            >
              <div className={S.sampleMeta}>
                <span className={S.sampleIndex}>{sample.stage}</span>
                <div className={S.sampleText}>
                  <h5 className={S.sampleTitle}>{sample.title}</h5>
                  <p className={S.sampleDescription}>{sample.description}</p>
                </div>
                <span className={S.sampleLevel}>{sample.level}%</span>
              </div>
              <div className={S.mobileStageMeter} aria-hidden="true">
                <div className={S.mobileStageTrack}>
                  <div
                    className={S.mobileStageFill}
                    style={{ width: `${sample.level}%` }}
                  />
                </div>
                <div className={S.mobileStageLabels}>
                  <span>{t('정상')}</span>
                  <span>{t('불량')}</span>
                </div>
              </div>
              <IndustrialWaveformPlayer
                sampleId={sample.analyticsId}
                src={sample.src}
                label={sample.title}
                t={t}
              />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
